import { Alert, Button, Input, Modal, Select, Tabs } from 'antd'
import { isArray, isString } from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { faEnvelope, faUser } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import DataTable from '../antd/Tables/DataTable/DataTable'
import { DeleteAction } from '../antd/Tables/DataTable/utils/actions'
import FormItem from '../shared/Forms/FormItem'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { getInstitutionUsers, updateInstitutionOption } from '../../utils/api/institution'
import { selectUser } from '../../reducers/UserReducer'
import { isObject } from '../../utils'
import { onSuccess } from '../../utils/apiHelper'
import { InstitutionContext } from '../../Providers/InstitutionProvider'

const INTERNAL_USERS = 'internal_users'
const EXTERNAL_USERS = 'external_users'

const OptionEmailsForm = ({ option }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const { updateOption } = useContext(InstitutionContext)

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(option)
  const [institutionUsers, setInstitutionUsers] = useState([])
  const [visibleExternalModal, setVisibleExternalModal] = useState(false)
  const [visibleInternalModal, setVisibleInternalModal] = useState(false)
  const [form, setForm] = useState({})
  const [usersSearch, setUsersSearch] = useState('')

  const isValidEmail = email => {
    let isValid = false

    if (isString(email)) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      isValid = re.test(email)
    }

    return isValid
  }

  useEffect(() => {
    let mounted = true

    if (isObject(user) && isObject(option?.institution)) {
      getInstitutionUsers(user, option.institution, { archived: false }).then(response => {
        if (mounted) {
          setInstitutionUsers(response.data)
        }

        setLoading(false)
      })
    }

    return () => { mounted = false }
  }, [user, option?.institution])

  const externalsValues = useMemo(() => {
    let values = []

    if (isObject(data.params) && isArray(data.params.externals)) {
      values = data.params.externals.map(({ name, email }, index) => ({
        key: index,
        name: name,
        email: email
      }))
    }

    return values
  }, [data.params])

  const existingUserIds = useMemo(() => {
    return isObject(data.params) && isArray(data.params.users) ? data.params.users : []
  }, [data.params])

  const internalsValues = useMemo(() => {
    let values = []

    if (isObject(data.params) && isArray(data.params.users)) {
      values = institutionUsers.filter(u => data.params.users.includes(u.id))
    }

    return values.map(user => ({
      key: user.id,
      name: user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : '/',
      email: user.email
    }))
  }, [data.params, institutionUsers])

  const handleDelete = (value, type) => {
    if (type === EXTERNAL_USERS) {
      const newExternals = externalsValues.filter(ev => ev.key !== value.key).map(ev => ({ name: ev.name, email: ev.email }))
      const newData = {
        ...data,
        params: {
          ...data.params,
          externals: newExternals
        }
      }
      setData(newData)
      updateInstitutionOption(user, newData, { params: newData.params }).then(response => {
        onSuccess(t('institution.option.update.success'))
        updateOption(response.data)
      })
    } else if (type === INTERNAL_USERS) {
      const newUsers = internalsValues.filter(iv => iv.key !== value.key).map(iv => iv.key)
      const newData = {
        ...data,
        params: {
          ...data.params,
          users: newUsers
        }
      }
      setData(newData)
      updateInstitutionOption(user, newData, { params: newData.params }).then(response => {
        onSuccess(t('institution.option.update.success'))
        updateOption(response.data)
      })
    }
  }

  const handleAdd = (type, payload) => {
    if (type === EXTERNAL_USERS) {
      const name = (form.name || '').trim()
      const email = (form.email || '').trim().toLowerCase()

      if (!name || !email) {
        setForm({
          ...form,
          error: t('institution.option.warning.fields_required')
        })
        return
      }

      if (!isValidEmail(email)) {
        setForm({
          ...form,
          error: t('institution.option.warning.invalid_email')
        })
        return
      }

      const existingEmails = externalsValues.map(ev => (ev.email || '').toLowerCase())
      if (existingEmails.includes(email)) {
        setForm({
          ...form,
          error: t('institution.option.warning.email_exists')
        })
        return
      }

      const newExternals = [...externalsValues, { name, email }].map(ev => ({ name: ev.name, email: ev.email }))
      const newData = {
        ...data,
        params: {
          ...data.params,
          externals: newExternals
        }
      }
      setData(newData)
      setVisibleExternalModal(false)
      updateInstitutionOption(user, newData, { params: newData.params }).then(response => {
        updateOption(response.data)
        onSuccess(t('institution.option.update.success'))
        setForm({})
      })
    } else if (type === INTERNAL_USERS) {
      const userId = payload || form.userId
      if (!userId) {
        setForm({
          ...form,
          error: t('institution.option.warning.select_user')
        })
        return
      }

      const newUsers = [...(data.params?.users || []), userId]
      const newData = {
        ...data,
        params: {
          ...data.params,
          users: newUsers
        }
      }
      setData(newData)
      updateInstitutionOption(user, newData, { params: newData.params }).then(response => {
        updateOption(response.data)
        onSuccess(t('institution.option.update.success'))
        setVisibleInternalModal(false)
        setForm({})
        setUsersSearch('')
      })
    }
  }

  const externalsColumns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email'
    }
  ]

  const externalsActions = [
    new DeleteAction({
      title: t('common.delete'),
      onDelete: (value) => handleDelete(value, EXTERNAL_USERS)
    })
  ]

  const internalsColumns = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: t('common.email'),
      dataIndex: 'email',
      key: 'email'
    }
  ]

  const internalsActions = [
    new DeleteAction({
      title: t('common.delete'),
      onDelete: (user) => handleDelete(user, INTERNAL_USERS)
    })
  ]

  return (
    <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      <Tabs>
        <Tabs.TabPane tab={<div><FontAwesomeIcon icon={faUser} /> <span> {t('institution.option.tabs.internals')}</span></div>} key='1'>
          <DataTable
            loading={loading}
            columns={internalsColumns}
            data={internalsValues}
            actions={internalsActions}
            onCreate={() => setVisibleInternalModal(true)}
            removeConfig
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <div>
              <FontAwesomeIcon icon={faEnvelope} /> <span> {t('institution.option.tabs.externals')}</span>
            </div>
          } key='2'
        >
          <DataTable
            columns={externalsColumns}
            actions={externalsActions}
            data={externalsValues}
            onCreate={() => setVisibleExternalModal(true)}
            removeConfig
          />
        </Tabs.TabPane>
      </Tabs>
      <Modal
        visible={visibleExternalModal}
        title={t('institution.option.add_external_user')}
        footer={null}
        onCancel={() => setVisibleExternalModal(false)}
        centered
      >
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          <FormItem label='Name' icon={faUser}>
            <Input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value, error: undefined })}
              required
            />
          </FormItem>
          <FormItem label='Email' icon={faEnvelope}>
            <Input
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value, error: undefined })}
              required
            />
          </FormItem>
        </div>
        {form && form.error && (<div style={{ display: 'flex', justifyContent: 'center ' }}><Alert showIcon style={{ marginBottom: '10px', width: '70%', textAlign: 'center' }} type='error' message={form.error} /></div>)}
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          <Button type='primary' onClick={() => handleAdd(EXTERNAL_USERS)}>
            {t('common.add')}
          </Button>
        </div>
      </Modal>
      <Modal
        visible={visibleInternalModal}
        title={t('institution.option.add_internal_user')}
        footer={null}
        onCancel={() => { setVisibleInternalModal(false); setUsersSearch(''); setForm({}) }}
        centered
      >
        <Select
          style={{ width: '100%' }}
          placeholder={t('institution.option.select_user')}
          showSearch
          filterOption={false}
          onSearch={(value) => setUsersSearch(value)}
          onChange={(value) => {
            handleAdd(INTERNAL_USERS, value)
          }}
        >
          {institutionUsers.filter(u => {
            let toKeep = !existingUserIds.includes(u.id)

            const search = (usersSearch || '').toLowerCase()
            if (toKeep && search) {
              const fullName = `${u.firstname} ${u.lastname}`.toLowerCase()

              toKeep = fullName.localeCompare(search) || u.email.toLowerCase().localeCompare(search)
            }

            return toKeep
          }).map(user => (
            <Select.Option key={user.id} value={user.id}>
              {user.firstname && user.lastname ? `${user.firstname} ${user.lastname} - ` : ''} {user.email}
            </Select.Option>
          ))}
        </Select>
        {form && form.error && (<div style={{ display: 'flex', justifyContent: 'center ' }}><Alert showIcon style={{ marginBottom: '10px', width: '70%', textAlign: 'center' }} type='error' message={form.error} /></div>)}
      </Modal>
    </div>
  )
}

export default OptionEmailsForm
